<template>
  <Teleport to="body">
    <Transition name="modal" appear>
      <div 
        v-if="isVisible" 
        class="base-modal-overlay" 
        @click="handleOverlayClick"
        @keydown.esc="handleEscapeKey"
        tabindex="-1"
        ref="overlayRef"
      >
        <div 
          class="base-modal-content" 
          @click="handleModalContentClick"
          :class="modalClasses"
          :style="modalStyles"
          role="dialog"
          :aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
        >
          <!-- Modal Header -->
          <div v-if="showHeader" class="base-modal-header">
            <div class="base-modal-title-section">
              <div v-if="icon" class="base-modal-icon" :class="iconClasses">
                <font-awesome-icon :icon="icon" />
              </div>
              <h3 :id="titleId" class="base-modal-title">{{ title }}</h3>
            </div>
            <button 
              v-if="closable" 
              @click="closeModal" 
              class="base-modal-close"
              :aria-label="closeAriaLabel"
              data-test="modal-close-button"
            >
              <font-awesome-icon icon="times" />
            </button>
          </div>

          <!-- Modal Body -->
          <div class="base-modal-body" :id="descriptionId">
            <slot></slot>
          </div>

          <!-- Modal Footer -->
          <div v-if="showFooter" class="base-modal-footer">
            <slot name="footer">
              <div class="base-modal-actions">
                <BaseButton 
                  v-if="showCancelButton"
                  :text="cancelText"
                  variant="outline"
                  size="medium"
                  @click="handleCancel"
                  data-test="modal-cancel-button"
                />
                <BaseButton 
                  v-if="showConfirmButton"
                  :text="confirmText"
                  :variant="confirmVariant"
                  :loading="loading"
                  :disabled="disabled"
                  size="medium"
                  @click="handleConfirm"
                  data-test="modal-confirm-button"
                />
              </div>
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
/**
 * BaseModal - A modern, accessible modal component
 * 
 * This component provides a flexible modal implementation with support for:
 * - Modern design with smooth animations
 * - Full accessibility support (ARIA attributes, keyboard navigation, focus management)
 * - Customizable appearance (size, variant, icon)
 * - Flexible content slots
 * - Built-in action buttons with loading states
 * - Escape key and overlay click handling
 * - Teleported rendering for proper z-index stacking
 * 
 * @component
 * @example
 * <!-- Basic confirmation modal -->
 * <BaseModal 
 *   :visible="showModal"
 *   title="Confirm Action"
 *   confirm-text="Yes, Continue"
 *   cancel-text="Cancel"
 *   @confirm="handleConfirm"
 *   @cancel="handleCancel"
 * >
 *   <p>Are you sure you want to proceed with this action?</p>
 * </BaseModal>
 * 
 * @example
 * <!-- Custom modal with icon and danger variant -->
 * <BaseModal 
 *   :visible="showDeleteModal"
 *   title="Delete Item"
 *   icon="trash-alt"
 *   variant="danger"
 *   confirm-text="Delete"
 *   cancel-text="Cancel"
 *   :loading="isDeleting"
 *   @confirm="handleDelete"
 *   @cancel="handleCancel"
 * >
 *   <p>This action cannot be undone. Are you sure?</p>
 * </BaseModal>
 * 
 * @example
 * <!-- Custom footer slot -->
 * <BaseModal 
 *   :visible="showCustomModal"
 *   title="Custom Actions"
 *   @cancel="handleCancel"
 * >
 *   <p>Modal content here</p>
 *   <template #footer>
 *     <div class="custom-actions">
 *       <BaseButton text="Save Draft" variant="outline" />
 *       <BaseButton text="Publish" variant="primary" />
 *     </div>
 *   </template>
 * </BaseModal>
 */
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import BaseButton from './BaseButton.vue'

export default {
  name: 'BaseModal',
  
  components: {
    BaseButton
  },
  
  props: {
    /**
     * Whether the modal is visible
     * @type {Boolean}
     */
    visible: {
      type: Boolean,
      default: false
    },
    
    /**
     * Modal title
     * @type {String}
     */
    title: {
      type: String,
      default: ''
    },
    
    /**
     * Icon class for the modal header
     * @type {String}
     */
    icon: {
      type: String,
      default: ''
    },
    
    /**
     * Visual variant of the modal
     * @type {String}
     * @values 'default', 'danger', 'warning', 'success', 'info'
     */
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'danger', 'warning', 'success', 'info'].includes(value)
    },
    
    /**
     * Size of the modal
     * @type {String}
     * @values 'small', 'medium', 'large', 'extra-large'
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large', 'extra-large'].includes(value)
    },
    
    /**
     * Whether the modal can be closed by clicking the overlay or pressing escape
     * @type {Boolean}
     */
    closable: {
      type: Boolean,
      default: true
    },
    
    /**
     * Whether to show the header section
     * @type {Boolean}
     */
    showHeader: {
      type: Boolean,
      default: true
    },
    
    /**
     * Whether to show the footer section
     * @type {Boolean}
     */
    showFooter: {
      type: Boolean,
      default: true
    },
    
    /**
     * Whether to show the cancel button
     * @type {Boolean}
     */
    showCancelButton: {
      type: Boolean,
      default: true
    },
    
    /**
     * Whether to show the confirm button
     * @type {Boolean}
     */
    showConfirmButton: {
      type: Boolean,
      default: true
    },
    
    /**
     * Text for the cancel button
     * @type {String}
     */
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    
    /**
     * Text for the confirm button
     * @type {String}
     */
    confirmText: {
      type: String,
      default: 'Confirm'
    },
    
    /**
     * Variant for the confirm button
     * @type {String}
     */
    confirmVariant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'outline', 'danger', 'success', 'warning'].includes(value)
    },
    
    /**
     * Whether the confirm button is in loading state
     * @type {Boolean}
     */
    loading: {
      type: Boolean,
      default: false
    },
    
    /**
     * Whether the confirm button is disabled
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false
    },
    
    /**
     * Custom CSS classes for the modal content
     * @type {String}
     */
    customClass: {
      type: String,
      default: ''
    },
    
    /**
     * Custom styles for the modal content
     * @type {Object}
     */
    customStyle: {
      type: Object,
      default: () => ({})
    },
    
    /**
     * ARIA label for the close button
     * @type {String}
     */
    closeAriaLabel: {
      type: String,
      default: 'Close modal'
    }
  },
  
  emits: [
    'update:visible',
    'confirm',
    'cancel',
    'close'
  ],
  
  setup(props, { emit }) {
    const overlayRef = ref(null)
    const isVisible = computed(() => props.visible)
    
    // Generate unique IDs for accessibility
    const titleId = computed(() => `modal-title-${Math.random().toString(36).substr(2, 9)}`)
    const descriptionId = computed(() => `modal-description-${Math.random().toString(36).substr(2, 9)}`)
    
    // Modal classes
    const modalClasses = computed(() => [
      `base-modal--${props.size}`,
      `base-modal--${props.variant}`,
      props.customClass
    ].filter(Boolean))
    
    // Modal styles
    const modalStyles = computed(() => ({
      ...props.customStyle
    }))
    
    // Icon classes
    const iconClasses = computed(() => [
      `base-modal-icon--${props.variant}`
    ])
    
    // Focus management
    let previousActiveElement = null
    
    const trapFocus = (event) => {
      if (!isVisible.value) return
      
      const modal = overlayRef.value?.querySelector('.base-modal-content')
      if (!modal) return
      
      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus()
            event.preventDefault()
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus()
            event.preventDefault()
          }
        }
      }
    }
    
    const setInitialFocus = () => {
      nextTick(() => {
        const modal = overlayRef.value?.querySelector('.base-modal-content')
        if (modal) {
          const firstFocusable = modal.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
          firstFocusable?.focus()
        }
      })
    }
    
    const restoreFocus = () => {
      if (previousActiveElement) {
        previousActiveElement.focus()
        previousActiveElement = null
      }
    }
    
    // Event handlers
    const handleOverlayClick = () => {
      // Prevent closing when clicking outside the modal
      // Users must use the X button or ESC key to close
    }
    
    const handleModalContentClick = (event) => {
      // Check if click is outside any BaseSelect components BEFORE stopping propagation
      // BaseSelect uses .base-select class for the main wrapper and .select-dropdown--teleported for dropdown
      const clickedInsideSelect = event.target.closest('.base-select')
      const clickedInsideDropdown = event.target.closest('.select-dropdown--teleported')
      
      // Stop propagation to prevent closing modal on content clicks
      event.stopPropagation()
      
      // If click is not inside a select or its dropdown, close any open BaseSelect dropdowns
      // by finding all open selects in the modal and triggering their close
      if (!clickedInsideSelect && !clickedInsideDropdown) {
        // Find all BaseSelect components that are currently open
        const openSelects = overlayRef.value?.querySelectorAll('.base-select.is-open')
        if (openSelects && openSelects.length > 0) {
          // For each open select, find its button and trigger a blur/close
          openSelects.forEach(selectEl => {
            const selectButton = selectEl.querySelector('.select-button')
            if (selectButton) {
              // Dispatch a click event directly on the button to toggle it closed
              selectButton.click()
            }
          })
        }
      }
    }
    
    const handleEscapeKey = () => {
      if (props.closable) {
        closeModal()
      }
    }
    
    const closeModal = () => {
      emit('update:visible', false)
      emit('close')
      restoreFocus()
    }
    
    const handleCancel = () => {
      emit('cancel')
      closeModal()
    }
    
    const handleConfirm = () => {
      emit('confirm')
    }
    
    // Watch for visibility changes
    watch(isVisible, (newValue) => {
      if (newValue) {
        previousActiveElement = document.activeElement
        setInitialFocus()
        document.addEventListener('keydown', trapFocus)
        
        // Keep scroll visible - don't set overflow: hidden
        // This allows users to scroll the page even when modal is open
        // and prevents the layout shift from scrollbar disappearing
      } else {
        document.removeEventListener('keydown', trapFocus)
        restoreFocus()
      }
    })
    
    // Cleanup on unmount
    onUnmounted(() => {
      document.removeEventListener('keydown', trapFocus)
    })
    
    return {
      overlayRef,
      isVisible,
      titleId,
      descriptionId,
      modalClasses,
      modalStyles,
      iconClasses,
      handleOverlayClick,
      handleModalContentClick,
      handleEscapeKey,
      closeModal,
      handleCancel,
      handleConfirm
    }
  }
}
</script>

<style scoped>
/* Modal Overlay */
.base-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

/* Modal Content */
.base-modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  outline: none;
}

/* Modal Sizes */
.base-modal--small {
  width: 90%;
  max-width: 400px;
}

.base-modal--medium {
  width: 90%;
  max-width: 500px;
}

.base-modal--large {
  width: 90%;
  max-width: 700px;
}

.base-modal--extra-large {
  width: 90%;
  max-width: 900px;
}

/* Modal Variants */
.base-modal--danger .base-modal-header {
  border-bottom-color: #fecaca;
}

.base-modal--warning .base-modal-header {
  border-bottom-color: #fed7aa;
}

.base-modal--success .base-modal-header {
  border-bottom-color: #bbf7d0;
}

.base-modal--info .base-modal-header {
  border-bottom-color: #bfdbfe;
}

/* Modal Header */
.base-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.base-modal-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.base-modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  font-size: 18px;
}

.base-modal-icon--default {
  background-color: #f3f4f6;
  color: #6b7280;
}

.base-modal-icon--danger {
  background-color: #fef2f2;
  color: #dc2626;
}

.base-modal-icon--warning {
  /* background-color: #fffbeb;
  color: #d97706; */
}

.base-modal-icon--success {
  background-color: #f0fdf4;
  color: #16a34a;
}

.base-modal-icon--info {
  background-color: #eff6ff;
  color: #2563eb;
}

.base-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.2;
}

.base-modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.base-modal-close:hover {
  background-color: #f3f4f6;
  color: #374151;
}

/* .base-modal-close:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
} */

/* Modal Body */
.base-modal-body {
  padding: 0 24px 24px 24px;
  color: #374151;
  line-height: 1.6;
}

/* Modal Footer */
.base-modal-footer {
  padding: 0 24px 24px 24px;
  border-top: 1px solid #e5e7eb;
  margin-top: 24px;
  padding-top: 24px;
}

.base-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Animations */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.modal-enter-active .base-modal-content,
.modal-leave-active .base-modal-content {
  transition: all 0.3s ease;
}

.modal-enter-from .base-modal-content {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

.modal-leave-to .base-modal-content {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

/* Responsive adjustments */
@media screen and (max-width: 640px) {
  .base-modal-content {
    margin: 16px;
    max-height: calc(100vh - 32px);
  }
  
  .base-modal-header,
  .base-modal-body,
  .base-modal-footer {
    padding-left: 20px;
    padding-right: 20px;
  }
  
  .base-modal-actions {
    flex-direction: column-reverse;
  }
  
  .base-modal-actions .base-button {
    width: 100%;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .base-modal-content {
    background: #1f2937;
    color: #f9fafb;
  }
  
  .base-modal-title {
    color: #f9fafb;
  }
  
  .base-modal-body {
    color: #d1d5db;
  }
  
  .base-modal-header,
  .base-modal-footer {
    border-color: #374151;
  }
  
  .base-modal-icon--default {
    background-color: #374151;
    color: #9ca3af;
  }
  
  .base-modal-close {
    color: #9ca3af;
  }
  
  .base-modal-close:hover {
    background-color: #374151;
    color: #d1d5db;
  }
}
</style>
